import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  ButtonGroup,
  chakra,
  HStack,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { Alert } from '@gamma/ui/overlay';
import { DataTable, DataTablePageSize } from '@gamma/ui/data-table';
import AccountCircleSharpIcon from '@gamma/ui/icons/AccountCircleSharpIcon';
import KeyRoundedIcon from '@gamma/ui/icons/KeyRoundedIcon';
import WarningSharpIcon from '@gamma/ui/icons/WarningSharpIcon';
import { AccordionPanel, FlushPanelContent, Panel } from '@gamma/ui/layout';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { TimestampCell } from '@investigator/shared/components';
import {
  graphqlErrorRedirects,
  TOTAL_API_KEY_DEFINITIONS_LIMIT,
} from '@investigator/shared/constants';
import { ApiKeyDefinition } from '@investigator/shared/generated/graphql';
import { useUserSession } from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { Column } from 'react-table';
import moment from 'moment';
import { useMemo, useState, useCallback } from 'react';
import { useBoolean } from '@chakra-ui/react';
import { useGetAPIKeys } from '../../queries';
import { AddAPIKey } from '../AddAPIKey';
import { RevokeAPIKey } from '../RevokeAPIKey';
import { RotateAPIKey } from '../RotateAPIKey';

const {
  title,
  tableColumns,
  noData,
  description,
  learnMore,
  na,
  detections,
  alerts,
  read,
  logs,
  access,
  expiredTooltip,
  expiringTooltip,
  expirationAnnouncement,
} = i18n.pages.apiKeys;

interface Action {
  id: string;
  name: string;
}

interface Permission {
  type: string;
  accesses: string[];
}

type APIKeyRow = Partial<ApiKeyDefinition> & {
  permissions: Permission[];
  actions: Action;
};

export const APIKeysDashboard = () => {
  const { signOut } = useUserSession();

  const [isAnnouncementVisible, setIsAnnouncementVisible] = useBoolean(true);

  //Pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState<DataTablePageSize>(10);
  const offset = pageIndex * pageSize;

  const { loading, data, error } = useGetAPIKeys({
    variables: {
      size: pageSize,
      offset: offset,
    },
  });

  const apiKeyDefinitions =
    data?.listAPIKeyDefinitions?.api_key_definitions?.filter(Boolean) || [];

  const totalItems = data?.listAPIKeyDefinitions?.total_count || 0;
  const pageCount = Math.ceil(totalItems / pageSize) || 1;

  const handleFetchData = useCallback(
    (newPageIndex: number, newPageSize: number) => {
      setPageIndex(newPageIndex);
      setPageSize(newPageSize as DataTablePageSize);
    },
    [],
  );

  const handlePageChange = useCallback((newPageIndex: number) => {
    setPageIndex(newPageIndex);
  }, []);

  const handlePageSizeChange = useCallback((size: DataTablePageSize) => {
    setPageSize(size);
    setPageIndex(0); // Reset to first page when changing page size
  }, []);

  const apiKeysData: APIKeyRow[] = apiKeyDefinitions.map((apiKey) => {
    const permissions = [
      {
        type: detections,
        accesses: [read],
      },
      {
        type: alerts,
        accesses: [read],
      },

      {
        type: logs,
        accesses: [read],
      },
    ];

    return {
      id: apiKey?.id || undefined,
      name: apiKey?.name || undefined,
      updated_ts: apiKey?.updated_ts || undefined,
      created_ts: apiKey?.created_ts || undefined,
      end_ts: apiKey?.end_ts || undefined,
      created_by_alias: apiKey?.created_by_alias || undefined,
      actions: { id: apiKey?.id || '', name: apiKey?.name || '' },
      permissions,
    };
  });

  const dataTableColumns: Column<APIKeyRow>[] = [
    {
      Header: tableColumns.name,
      accessor: 'name',
      disableSortBy: true,
      Cell: (props) => <Text>{props.value}</Text>,
    },
    {
      Header: tableColumns.permissions,
      accessor: 'permissions',
      disableSortBy: true,
      Cell: (props) => (
        <VStack align={'flex-start'}>
          {props.value.map((permission, idx) => (
            <Text fontWeight={'bold'} key={permission.type || idx}>
              {permission.type}{' '}
              <chakra.span fontWeight="normal">
                {permission.accesses.join(', ')}{' '}
                {permission.accesses.length > 0 && access}
              </chakra.span>
            </Text>
          ))}
        </VStack>
      ),
    },
    {
      Header: tableColumns.lastUsed,
      disableSortBy: true,
      accessor: 'updated_ts',
      Cell: (props) =>
        props.value != null ? (
          <TimestampCell
            timestamp={props.value}
            isFromNow={false}
            format="ll"
          />
        ) : (
          <Text>{na}</Text>
        ),
    },
    {
      Header: tableColumns.createdDate,
      disableSortBy: true,
      accessor: 'created_ts',
      Cell: (props) =>
        props.value != null ? (
          <TimestampCell
            timestamp={props.value}
            isFromNow={false}
            format="ll"
          />
        ) : (
          <Text>{na}</Text>
        ),
    },
    {
      Header: tableColumns.expirationDate,
      disableSortBy: true,
      accessor: 'end_ts',
      Cell: (props) => {
        const nowInSeconds = useMemo(() => moment().unix(), []);
        const thirtyDaysFromNow = useMemo(
          () => moment().add(30, 'd').unix(),
          [],
        );
        const isExpired = props.value != null && props.value <= nowInSeconds;
        const is30DaysFromExpiry =
          props.value != null && props.value <= thirtyDaysFromNow;

        return props.value != null ? (
          <HStack spacing={2} align="center">
            {(isExpired || is30DaysFromExpiry) && (
              <Tooltip
                label={isExpired ? expiredTooltip : expiringTooltip}
                placement="bottom"
                aria-label={
                  isExpired ? 'API key has expired' : 'API key is expiring'
                }
              >
                <Box
                  display="flex"
                  alignItems="center"
                  data-testid="warning-icon"
                >
                  <WarningSharpIcon color="orange.500" w="16px" h="16px" />
                </Box>
              </Tooltip>
            )}
            <TimestampCell
              timestamp={props.value}
              isFromNow={false}
              format="ll"
            />
          </HStack>
        ) : (
          <Text>{na}</Text>
        );
      },
    },
    {
      Header: tableColumns.createdBy,
      disableSortBy: true,
      accessor: 'created_by_alias',
      Cell: (props) => {
        return props.value != null ? (
          <Tooltip
            label={props.value}
            placement="top"
            isDisabled={props?.value?.length <= 20}
          >
            <Tag size="sm" colorScheme="purple" variant="solid">
              <AccountCircleSharpIcon color="text.primary" />
              <TagLabel ml={2} isTruncated={props?.value?.length > 20}>
                {props.value}
              </TagLabel>
            </Tag>
          </Tooltip>
        ) : (
          na
        );
      },
    },
    {
      Header: tableColumns.actions,
      disableSortBy: true,
      accessor: 'actions',
      Cell: (props) => (
        <ButtonGroup>
          <RotateAPIKey
            id={props.value.id}
            name={props.value.name}
            end_ts={props.row.original.end_ts}
          />
          <RevokeAPIKey id={props.value.id} name={props.value.name} />
        </ButtonGroup>
      ),
    },
  ];

  return loading || error ? (
    <Panel>
      <GraphQLReqStatus
        loading={loading}
        error={error}
        signOut={signOut}
        extended={graphqlErrorRedirects}
      />
    </Panel>
  ) : (
    <>
      <Box marginBottom={4}>
        <Alert
          status="info"
          variant="solid"
          title={expirationAnnouncement.title}
          isClosable={true}
          isOpen={isAnnouncementVisible}
          onClose={setIsAnnouncementVisible.off}
          data-testid="expiration-announcement-banner"
        >
          {expirationAnnouncement.description}
        </Alert>
      </Box>
      <AccordionPanel
        title={
          <HStack gap={3} align={'center'} justifyContent={'center'}>
            <Text>{title}</Text>
            <Popover trigger="hover" placement="right-start">
              <PopoverTrigger>
                <InfoOutlineIcon
                  w={'18px'}
                  h="18px"
                  onClick={(e) => e.stopPropagation()}
                />
              </PopoverTrigger>
              <PopoverContent
                w={'25%'}
                h={'auto'}
                onClick={(e) => e.stopPropagation()}
              >
                <PopoverHeader
                  border="0"
                  display={'inline-flex'}
                  justifyContent={'flex-start'}
                  alignItems={'center'}
                  gap={2}
                >
                  <KeyRoundedIcon
                    transform={'auto'}
                    scaleX={1.25}
                    scaleY={1.25}
                  />
                  {title}
                </PopoverHeader>
                <PopoverBody borderRadius="5px">
                  <Text
                    align={'left'}
                    fontWeight={'normal'}
                    fontSize={'smaller'}
                  >
                    {description}
                    <br />
                    <Link
                      isExternal
                      color="text.link"
                      textDecoration="none"
                      href="../docs/api.html"
                    >
                      {learnMore}
                    </Link>
                  </Text>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </HStack>
        }
        reduceMotion={true}
        storageKey="api-keys-panel"
        data-testid="api-keys-panel"
        ActionButton={
          <AddAPIKey
            isDisabled={totalItems >= TOTAL_API_KEY_DEFINITIONS_LIMIT}
          />
        }
      >
        <FlushPanelContent>
          <Box
            css={{
              "div[role='cell']": {
                alignItems: 'flex-start',
              },
            }}
          >
            <DataTable
              data={apiKeysData}
              columns={dataTableColumns}
              isPaginable={true}
              isLined={true}
              isPaginationManual={true}
              autoResetPage={true}
              pageCount={pageCount}
              pageSize={pageSize}
              initialState={{
                pageIndex,
                pageSize,
              }}
              onFetchData={handleFetchData}
              onPageIndexChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              itemCount={totalItems}
              emptyBodyContent={
                <Text align={'center'} color="text.secondary">
                  {noData}
                </Text>
              }
            />
          </Box>
        </FlushPanelContent>
      </AccordionPanel>
    </>
  );
};
